import { useMutation } from "@tanstack/react-query";
import { Hash, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { apiFetch } from "@/lib/auth-api";
import { badgeBase, statusClasses } from "@/lib/colors";
import { cn } from "@/lib/utils";
import type { DeviceAgent } from "./devices-api";

const nameOf = (d: Pick<DeviceAgent, "displayName" | "hostname" | "deviceUuid">) =>
	d.displayName ?? d.hostname ?? d.deviceUuid;

/** Wersja agenta, od której stanowisko umie pokazać numer w rogu ekranu. */
export const IDENTIFY_MIN_AGENT_VERSION = "0.1.139";

type IdentifyStatus = "sent" | "offline" | "too_old" | "not_found";

type NumberedDevice = Pick<
	DeviceAgent,
	"deviceUuid" | "tenantId" | "idfirm" | "displayName" | "hostname"
>;

/** Numer z nazwy stanowiska: „ST2”, „ST3 Biuro”, „Stanowisko 3” (tak nazywa je wdrożenie). */
export function nameNumber(name: string | null | undefined): number | null {
	const m = name?.match(/(?:^|[^\p{L}\d])(?:st|stanowisko)\s*(\d{1,3})(?!\d)/iu);
	return m ? Number(m[1]) : null;
}

/**
 * Numer stanowiska w aptece: z nazwy („ST2” → 2). Stanowisko bez numeru w nazwie
 * albo z numerem zajętym przez inne dostaje kolejny wolny, po nazwie, potem UUID.
 * Liczony z pełnej listy urządzeń, więc ten sam komputer ma ten sam numer
 * w Urządzeniach, Drukarkach i Pracownikach.
 */
export function stationNumbers(devices: NumberedDevice[]): Map<string, number> {
	const byPharmacy = new Map<string, NumberedDevice[]>();
	for (const d of devices) {
		if (d.idfirm == null) continue;
		const key = `${d.tenantId ?? ""}:${d.idfirm}`;
		byPharmacy.set(key, [...(byPharmacy.get(key) ?? []), d]);
	}
	const numbers = new Map<string, number>();
	for (const group of byPharmacy.values()) {
		group.sort(
			(a, b) =>
				nameOf(a).localeCompare(nameOf(b), "pl", { numeric: true, sensitivity: "base" }) ||
				a.deviceUuid.localeCompare(b.deviceUuid),
		);
		const used = new Set<number>();
		const rest: NumberedDevice[] = [];
		for (const d of group) {
			const n = nameNumber(nameOf(d));
			if (n != null && n > 0 && !used.has(n)) {
				used.add(n);
				numbers.set(d.deviceUuid, n);
			} else rest.push(d);
		}
		let next = 1;
		for (const d of rest) {
			while (used.has(next)) next++;
			used.add(next);
			numbers.set(d.deviceUuid, next);
		}
	}
	return numbers;
}

/** Numer stanowiska obok nazwy — tylko gdy nazwa sama go nie mówi („ST2”). */
export function StationNumber({
	number,
	name,
}: {
	number: number | undefined;
	name: string | null | undefined;
}) {
	if (number == null || nameNumber(name) === number) return null;
	return (
		<span
			className={cn(badgeBase, statusClasses.info, "tabular-nums")}
			title="Numer stanowiska — „Identyfikuj” pokaże go w prawym górnym rogu ekranu"
		>
			#{number}
		</span>
	);
}

function summary(results: { status: IdentifyStatus }[]): string {
	const count = (s: IdentifyStatus) => results.filter((r) => r.status === s).length;
	const skipped = [
		count("offline") ? `${count("offline")} offline` : null,
		count("too_old")
			? `${count("too_old")} ze starszym agentem (wymaga ${IDENTIFY_MIN_AGENT_VERSION})`
			: null,
		count("not_found") ? `${count("not_found")} niedostępnych` : null,
	].filter(Boolean);
	const sent = count("sent");
	const head = sent
		? `Numer pokazany na ${sent} z ${results.length} stanowisk`
		: "Żadne stanowisko nie pokazało numeru";
	return skipped.length ? `${head}; pominięte: ${skipped.join(", ")}` : head;
}

/**
 * „Identyfikuj”: każde podane stanowisko pokazuje przez 15 s swój numer
 * w prawym górnym rogu ekranu, żeby personel wiedział, przy którym siedzi.
 */
export function IdentifyButton({
	devices,
	numbers,
	className,
}: {
	devices: DeviceAgent[];
	numbers: Map<string, number>;
	className?: string;
}) {
	const targets = devices.filter((d) => numbers.has(d.deviceUuid));
	const mutation = useMutation({
		mutationFn: () =>
			apiFetch<{ results: { deviceUuid: string; status: IdentifyStatus }[] }>(
				"/device-agent/identify",
				{
					method: "POST",
					body: JSON.stringify({
						devices: targets.map((d) => ({
							deviceUuid: d.deviceUuid,
							number: numbers.get(d.deviceUuid),
							label: nameOf(d),
						})),
					}),
				},
			),
		onSuccess: ({ results }) => {
			const text = summary(results);
			if (results.some((r) => r.status === "sent")) toast.success(text);
			else toast.error(text);
		},
		onError: () => toast.error("Nie udało się wysłać identyfikacji"),
	});
	if (targets.length === 0) return null;
	return (
		<Button
			type="button"
			size="sm"
			variant="outline"
			className={cn("h-7 gap-1 px-2 text-xs", className)}
			disabled={mutation.isPending}
			onClick={() => mutation.mutate()}
			title="Każde stanowisko pokaże swój numer w prawym górnym rogu ekranu przez 15 s"
		>
			{mutation.isPending ? (
				<Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
			) : (
				<Hash className="h-3.5 w-3.5" aria-hidden />
			)}
			Identyfikuj
		</Button>
	);
}
